import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/calendar/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/calendar/ui/form'
import { Input } from '@/components/calendar/ui/input'
import { Button } from '@/components/calendar/ui/button'
import { useCalendarContext } from '../calendar-context'
import { format } from 'date-fns'
import { DateTimePicker } from '@/components/calendar/ui/date-time-picker'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ColorPickerLabeled } from '@/components/calendar/ui/color-picker-labeled'
import { Combobox } from '@/components/calendar/ui/combobox'
import { getCurrencySymbol } from '@/lib/utils'
import { deleteReservation, getAvailableVehicles, getReservationStates, updateReservation } from '@/rental/services/reservation-service'
import { CalendarVehicle } from '../calendar-types'
import { toast } from 'sonner'
import { useUserContext } from '@/context/UserContext'

const formSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    start: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid start date',
    }),
    end: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid end date',
    }),
    color: z.string(),

    // Reservation details
    pickupId: z.number().min(1, 'Pickup location is required'),
    dropoffId: z.number().min(1, 'Dropoff location is required'),
    vehicleId: z.number().min(1, 'Vehicle is required'),
    price: z.string().optional(),
    previewPrice: z.string().optional(),
    discount: z.string().optional(),
    customerName: z.string().optional(),
    customerEmail: z.string().optional(),
    customerPhone: z.string().optional(),
    partnerId: z.number().optional(),
    notes: z.string().optional(),
    status: z.string().optional(),
  })
  .refine(
    (data) => {
      try {
        const start = new Date(data.start)
        const end = new Date(data.end)
        return end >= start
      } catch {
        return false
      }
    },
    {
      message: 'End time must be after start time',
      path: ['end'],
    }
  )

export default function CalendarManageEventDialog() {
  const {
    manageEventDialogOpen,
    setManageEventDialogOpen,
    selectedEvent,
    setSelectedEvent,
    events,
    setEvents,
    vehicles,
    locations,
    partners,
    reservationStates,
  } = useCalendarContext()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      start: '',
      end: '',
      color: 'blue',

      // Reservation details
      pickupId: undefined,
      dropoffId: undefined,
      vehicleId: undefined,
      previewPrice: '',
      discount: '',
      customerEmail: '',
      customerName: '',
      customerPhone: '',
      partnerId: undefined,
      notes: '',
      status: 'approved',
    },
  })

  const [availableVehicles, setAvailableVehicles] = useState<CalendarVehicle[]>([]);
  const [isCommissionPartner, setIsCommissionPartner] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [canUpdateStatus, setCanUpdateStatus] = useState<boolean>(false);
  const { decodedToken } = useUserContext();

  useEffect(() => {
    if (selectedEvent) {
      form.reset({
        title: selectedEvent.title,
        start: format(selectedEvent.start, "yyyy-MM-dd'T'HH:mm"),
        end: format(selectedEvent.end, "yyyy-MM-dd'T'HH:mm"),
        color: selectedEvent.color,

        // Reservation details
        pickupId: selectedEvent.pickupId,
        dropoffId: selectedEvent.dropoffId,
        vehicleId: selectedEvent.vehicleId,
        previewPrice: selectedEvent.price?.toString() || '',
        discount: selectedEvent.discount?.toString() || '',
        customerName: selectedEvent.customerName || '',
        customerEmail: selectedEvent.customerEmail || '',
        customerPhone: selectedEvent.customerPhone || '',
        partnerId: selectedEvent.partnerId || undefined,
        notes: selectedEvent.notes || '',
        status: selectedEvent.status,
      })

      const currency = getCurrencySymbol(decodedToken?.permissions?.currency || 'EUR');
      const currentUserId = decodedToken?.user?.id || undefined;
      const currentVehicle = vehicles.filter(v => v.id === selectedEvent.vehicleId);

      const userRole = decodedToken?.permissions?.user_role;
      const isPartner = userRole === 'commission_partner';

      if(isPartner) {
        setCanUpdateStatus(false);
        if(selectedEvent.status === 'pending' && selectedEvent.partnerId && selectedEvent.partnerId === currentUserId) {
          setCanEdit(true);
          setCanDelete(true);
        }
        else {
          setCanEdit(false);
          setCanDelete(false);
        }
      }
      else {
        setCanEdit(true);
        setCanDelete(true);
        setCanUpdateStatus(true);
      }

      setCurrency(currency);
      setIsCommissionPartner(isPartner);
      setAvailableVehicles(currentVehicle.length > 0 ? currentVehicle : []);
      calculatePrice(form.getValues());
    }
  }, [selectedEvent, form])

  async function loadVehicles() {
    const pickup = form.getValues().start;
    const dropOff = form.getValues().end;

    if (pickup && dropOff) {
      const pickupDate = new Date(pickup);
      const dropOffDate = new Date(dropOff);

      if (dropOffDate > pickupDate) {
        const vehicles = await getAvailableVehicles(new Date(pickup), new Date(dropOff));
        setAvailableVehicles(vehicles || []);
      }
    }
  }

  function calculatePrice(value: any) {
    const discount = parseFloat(value.discount || '0');
    const previewPrice = parseFloat(value.previewPrice || '');
    if (!isNaN(previewPrice)) {
      const finalPrice = discount > 0 ? previewPrice - (previewPrice * (discount / 100)) : previewPrice;
      form.setValue('price', finalPrice.toString());
    } else {
      form.setValue('price', '');
    }
  }

  form.watch((value, field) => {
    if (field.name === 'vehicleId') {
      const vehicleId = form.getValues().vehicleId;
      if (vehicleId) {
        const vehicle = availableVehicles.find(v => v.id === vehicleId);
        if (vehicle) {
          const price = vehicle.totalPrice;
          form.setValue('previewPrice', price ? price.toString() : '');
        }
      }
    }

    if (field.name === 'previewPrice' || field.name === 'discount') {
      calculatePrice(value);
    }
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {

      setIsLoading(true);

      if (!selectedEvent) return;

      const updatedEvent = {
        ...selectedEvent,
        title: vehicles.find(v => v.id === values.vehicleId)?.name || 'Updated Reservation',
        start: new Date(values.start),
        end: new Date(values.end),
        color: getReservationStates().find(x => x.value === values.status)?.color || 'blue',
        pickupId: values.pickupId,
        dropoffId: values.dropoffId,
        vehicleId: values.vehicleId,
        price: values.price ? parseFloat(values.price) : undefined,
        discount: values.discount ? parseFloat(values.discount) : undefined,
        customerName: values.customerName || '',
        customerEmail: values.customerEmail || '',
        customerPhone: values.customerPhone || '',
        partnerId: values.partnerId,
        notes: values.notes || '',
        status: values.status || 'pending',
      };

      // Update reservation details
      await updateReservation(updatedEvent);
      toast.success('Reservation updated successfully!');

      setEvents(
        events.map((event) =>
          event.id === selectedEvent.id ? updatedEvent : event
        )
      )
      handleClose()

    } catch (error: any) {
      console.error('Error updating reservation:', error);
      toast.error(error.respose?.data?.message || 'Failed to update reservation. Please try again later.');
    }
    finally {
      setIsLoading(false);
    }
  }

  async function handleDelete() {
    if (!selectedEvent) return;
    await deleteReservation(selectedEvent.id);
    toast.success('Reservation deleted successfully!');
    
    setEvents(events.filter((event) => event.id !== selectedEvent.id))
    handleClose()
  }

  function handleClose() {
    setManageEventDialogOpen(false)
    setSelectedEvent(null)
    form.reset()
  }

  return (
    <Dialog open={manageEventDialogOpen} onOpenChange={handleClose} modal={false}>
      <DialogContent className="max-w-2xl w-full">
        <DialogHeader>
          <DialogTitle>Manage reservation</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="gap-3 grid grid-cols-2 space-y-2">
            <FormField
              control={form.control}
              name="pickupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Pickup</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={locations} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dropoffId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Dropoff</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={locations} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Start</FormLabel>
                  <FormControl>
                    <DateTimePicker field={field} onChange={loadVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">End</FormLabel>
                  <FormControl>
                    <DateTimePicker field={field} onChange={loadVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicleId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Vehicle</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={availableVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isCommissionPartner &&
              <FormField
                control={form.control}
                name="partnerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Commision partner</FormLabel>
                    <FormControl>
                      <Combobox field={field} options={partners} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            }

            {isCommissionPartner &&
              <div></div>
            }

            <div className='grid grid-cols-2 gap-3'>
              <FormField
                control={form.control}
                name="previewPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Base price ({currency})</FormLabel>
                    <FormControl>
                      <Input type='number' placeholder="Base price" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Discount(%)</FormLabel>
                    <FormControl>
                      <Input type='number' min={0} max={100} placeholder="Discount %" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold" style={{ color: "green" }}>Price ({currency})</FormLabel>
                  <FormControl>
                    <Input type='number' readOnly placeholder="Base price" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Customer name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Email</FormLabel>
                  <FormControl>
                    <Input type='email' placeholder="Customer email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Phone number</FormLabel>
                  <FormControl>
                    <Input placeholder="Customer phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Notes</FormLabel>
                  <FormControl>
                    <Input placeholder="Notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {canUpdateStatus &&
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Status</FormLabel>
                    <FormControl>
                      <ColorPickerLabeled field={field} options={reservationStates} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            }

            <DialogFooter className="flex justify-between gap-2  col-span-2">
              {canDelete &&
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" type="button" disabled={isLoading}>
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this reservation? This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              }

              {canEdit &&
                <Button type="submit" disabled={isLoading || (!form.formState.isDirty)}>Update reservation</Button>
              }
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
